import { useCallback, useEffect, useRef } from 'react';

/**
 * Hook for managing delayed hover actions with timeout management.
 *
 * Provides `schedule` and `clear` functions to trigger an action after a delay,
 * useful for implementing hover interactions that should only activate after
 * the user has hovered for a certain duration (e.g., showing a profile card
 * after hovering over a name for 500ms).
 */
function useHoverDelay(callback: () => void, delay: number) {
	const timeoutRef = useRef<number | null>(null);

	// Ref pattern keeps `schedule` stable while always calling latest callback
	const callbackRef = useRef(callback);

	useEffect(() => {
		callbackRef.current = callback;
	}, [callback]);

	const clear = useCallback((): void => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	const schedule = useCallback((): void => {
		clear();
		timeoutRef.current = window.setTimeout(() => {
			callbackRef.current();
		}, delay);
	}, [delay, clear]);

	// Cleanup on unmount
	useEffect(() => {
		return () => clear();
	}, [clear]);

	return { schedule, clear };
}

export { useHoverDelay };
